import React, { useState, useEffect } from "react";
import { Button, Form, Row, Steps, Typography } from "antd";
import axios from "axios";
import { message } from "components/Messages/Messages";
import { CompanyContact, Company } from "interfaces/companies";
import { useHistory } from "react-router-dom";
import { getBase64 } from "helpers/functions";
import useViewport from "components/UseViewport/UseViewport";
import Summary from "containers/CreateCompanyProfile/StepSummary/StepSummary";
import InfoAboutCompany from "./StepAboutCompany/StepAboutCompany";
import CompanyAccountDetails from "./StepAccountDetails/StepAccountDetails";
import CompanyContactInformation from "./StepContactInformation/StepContactInformation";

const { Title } = Typography;

export interface CreateCompanyData {
  name: string;
  secotrs: number[];
  size: number;
  description?: string;
  logo?: File;
  cover_image?: File;

  first_name: string;
  last_name: string;
  companyContactTelephone: string;
  companyContactEmail: string;
  position?: string;

  url?: string;
  organization_number: string;
  visitor_address?: string;
  visitor_area_code?: string;
  visitor_region?: string;
  country?: string;
  telephone?: string;
  email?: string;

  register_description: string;
  username: string;
  password: string;

  agreement: boolean;
}

const CompanyProfileMaking = () => {
  const { width } = useViewport();
  const breakpoint = 768;

  const [stepForm] = Form.useForm();
  const [stepNo, setStepNo] = useState(0);
  const [formValues, setFormValues] = useState<CreateCompanyData>();
  const [logo, setLogo] = useState<File | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const history = useHistory();

  useEffect(() => {
    if (logo) {
      getBase64(logo)
        .then(url => setLogoPreview(url))
        .catch(() => setLogoPreview(null));
    } else {
      setLogoPreview(null);
    }
    if (coverImage) {
      getBase64(coverImage)
        .then(url => setCoverImagePreview(url))
        .catch(() => setCoverImagePreview(null));
    } else {
      setCoverImagePreview(null);
    }
  }, [logo, coverImage]);

  const goToNextStep = () => {
    stepForm
      .validateFields()
      .then(values => {
        setFormValues({ ...formValues, ...values });
        if (stepNo === 3) return null;

        setStepNo(stepNo + 1);
      })
      .catch();
  };

  const goToPreviousStep = () => {
    if (stepNo === 0) return null;
    setStepNo(stepNo - 1);
  };

  const onFinish = async () => {
    setLoading(true);
    const formData = new FormData();
    if (formValues) {
      Object.entries(formValues).forEach(([key, value]) => {
        if (value instanceof Array) {
          value.forEach(val => formData.append(key, val));
          return;
        }
        formData.append(key, value as string);
      });
    }
    if (logo) formData.append("logo", logo);
    if (coverImage) formData.append("logo_wide", coverImage);

    try {
      const companyResponse = await axios.post<Company>("/companies/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const contactData = {
        first_name: formValues?.first_name,
        last_name: formValues?.last_name,
        telephone: formValues?.companyContactTelephone,
        email: formValues?.companyContactEmail,
        position: formValues?.position,
        company: companyResponse.data.id,
        main_contact: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      try {
        await axios.post<CompanyContact>(`/companies/${companyResponse.data.id}/contact-persons/`, contactData);
        message(
          "success",
          "Bedriftsprofil opprettet.",
          "Kontaktpersonen får en e-post når Bindeleddet har godkjent bedriften."
        );
        setLoading(false);
        history.push("/");
      } catch (contactError) {
        message(
          "error",
          "Feil med kontaktperson",
          "Bedriften ble opprettet, men det oppstod en feil under opprettelsen av kontaktpersonen. Ta kontakt med Bindeleddet for å registrere kontaktperson."
        );
        setLoading(false);
      }

      // TODO send mail til bedriftsgruppen når bedriften er opprettet
      setLoading(false);
      history.push("/");
      window.dispatchEvent(new Event("pushState")); // Trigger update av navbar
    } catch (error) {
      const errorResponse = ((error as unknown) as { response: { data: { username?: string[] } } }).response.data;
      errorResponse?.username?.includes("A user with that username already exists.")
        ? (message("error", "Brukernavnet er allerede i bruk."), setStepNo(2))
        : message("error", "Noe gikk galt under opprettelsen av bedriften.");
      setLoading(false);
    }
  };

  return (
    <>
      <Row style={width < breakpoint ? { width: "95%", margin: "0 auto" } : { width: "70%", margin: "0 auto" }}>
        <Title style={{ margin: "20px auto", textAlign: "center" }}>Opprett bedriftsprofil</Title>
        <Steps
          current={stepNo}
          labelPlacement="vertical"
          style={{ padding: "20px 0" }}
          items={[
            { title: "Om bedriften" },
            { title: "Kontaktinformasjon" },
            { title: "Kontodetaljer" },
            { title: "Oppsummering" },
          ]}
        />

        <Form form={stepForm} layout="vertical" onFinish={onFinish} style={{ width: "100%" }}>
          {stepNo === 0 && (
            <InfoAboutCompany
              setLogo={setLogo}
              setCoverImage={setCoverImage}
              logoPreview={logoPreview}
              coverImagePreview={coverImagePreview}
            />
          )}
          {stepNo === 1 && <CompanyContactInformation />}
          {stepNo === 2 && <CompanyAccountDetails />}
          {stepNo === 3 && <Summary values={formValues} />}

          <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
            {stepNo === 0 ? (
              <>
                <Button type="primary" danger size="large" onClick={() => history.push("/")}>
                  Avbryt
                </Button>
                <Button type="primary" size="large" onClick={() => goToNextStep()}>
                  Neste
                </Button>
              </>
            ) : stepNo === 1 || stepNo === 2 ? (
              <>
                <Button type="default" size="large" onClick={() => goToPreviousStep()}>
                  Forrige
                </Button>

                <Button type="primary" size="large" onClick={() => goToNextStep()}>
                  Neste
                </Button>
              </>
            ) : (
              <>
                <Button type="default" size="large" onClick={() => goToPreviousStep()}>
                  Forrige
                </Button>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    style={{ backgroundColor: "#59c09c", color: "var(--light)" }}
                    loading={loading}
                    htmlType="submit"
                  >
                    Fullfør
                  </Button>
                </Form.Item>
              </>
            )}
          </Row>
        </Form>
      </Row>
    </>
  );
};

export default CompanyProfileMaking;
